import { v4 as uuidv4 } from "uuid";
import type {
  CastMember,
  CastMemberType,
  ScheduleImportIssue,
  ScheduledScene,
  ScheduleLocationStatus,
  ScriptDayType,
  ShootDay,
  ShootDayType,
  ShootingSchedule,
  ShootWeek,
} from "../types";
import type { Scene, Sequence } from "../models/production";

const PARSER_VERSION = "shooting-schedule-parser-v1";
const EXTRACTION_VERSION = "shooting-schedule-extraction-v1";

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const cleanArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];

const parseInteger = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseInt(String(value).replace(/[^0-9-]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseFloatLike = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseFloat(String(value).replace(/[^0-9./ ]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeDayType = (value: unknown): ShootDayType => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "CWD") return "CWD";
  if (normalized === "SCWD") return "SCWD";
  return "unknown";
};

const normalizeScriptDayType = (value: unknown): ScriptDayType => {
  const normalized = String(value || "").trim().toUpperCase();
  if (["D", "N", "E", "DREAM", "VP", "SR"].includes(normalized)) {
    return normalized as ScriptDayType;
  }
  return "unknown";
};

const inferCastMemberType = (castCode: string, rawLabel?: string | null): CastMemberType => {
  const normalized = `${castCode} ${rawLabel || ""}`.toUpperCase();
  if (normalized.includes("DOG")) return "dog";
  if (normalized.includes("STUNT")) return "stunt";
  if (normalized.includes("INTIMACY")) return "intimacy";
  if (normalized.includes("OOV")) return "oov";
  if (normalized.includes("COORD")) return "coordinator";
  return "cast";
};

export const parseSceneNumberRule = (value: unknown) => {
  const original = String(value || "").trim();
  const matched = original.match(/^(\d+)\s*\/\s*(\d+[A-Z]?)$/i);
  if (!matched) {
    return {
      originalSceneNumber: original || null,
      episodeNumber: null,
      sceneNumberWithinEpisode: null,
      sceneIdNormalised: null,
    };
  }

  const episodeNumber = Number.parseInt(matched[1], 10);
  const sceneRaw = matched[2].toUpperCase();
  const sceneNumeric = Number.parseInt(sceneRaw.replace(/[^\d]/g, ""), 10);
  const suffix = sceneRaw.replace(/\d/g, "");

  return {
    originalSceneNumber: original,
    episodeNumber: Number.isFinite(episodeNumber) ? episodeNumber : null,
    sceneNumberWithinEpisode: Number.isFinite(sceneNumeric) ? sceneNumeric : null,
    sceneIdNormalised:
      Number.isFinite(episodeNumber) && Number.isFinite(sceneNumeric)
        ? `E${String(episodeNumber).padStart(2, "0")}_SC${String(sceneNumeric).padStart(3, "0")}${suffix}`
        : null,
  };
};

export const parseScriptPageEighths = (value: unknown): number | null => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const simpleFraction = raw.match(/^(\d+)\s*\/\s*8$/);
  if (simpleFraction) return Number.parseInt(simpleFraction[1], 10);

  const mixed = raw.match(/^(\d+)(?:\s+(\d+)\s*\/\s*8)?$/);
  if (mixed) {
    const wholePages = Number.parseInt(mixed[1], 10) * 8;
    const eighths = mixed[2] ? Number.parseInt(mixed[2], 10) : 0;
    return wholePages + eighths;
  }

  const decimal = parseFloatLike(raw);
  if (decimal !== null) {
    return Math.round(decimal * 8);
  }
  return null;
};

const normalizeCastCodes = (value: unknown) =>
  String(value || "")
    .split(/[,|/]/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const normalizeLocationStatus = (value: unknown): ScheduleLocationStatus => {
  const normalized = String(value || "").trim().toUpperCase();
  if (!normalized) return "unknown";
  if (normalized.includes("TBC")) return "tbc";
  if (normalized.includes("REPEAT")) return "repeat";
  if (normalized.includes("SUGGEST")) return "suggested";
  return "confirmed";
};

const EPISODE_WORDS: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
  SIX: 6,
  SEVEN: 7,
  EIGHT: 8,
  NINE: 9,
  TEN: 10,
  ELEVEN: 11,
  TWELVE: 12,
};

const parseEpisodeNumberFromValue = (value: unknown): number | null => {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return null;
  const digitMatch = raw.match(/(\d+)/);
  if (digitMatch) {
    const parsed = Number.parseInt(digitMatch[1], 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  for (const [word, number] of Object.entries(EPISODE_WORDS)) {
    if (raw.endsWith(word) || raw === word) return number;
  }
  return null;
};

const normalizeSceneWithinEpisode = (value: unknown) => {
  const raw = String(value || "").trim().toUpperCase();
  const matched = raw.match(/^(\d+)([A-Z]?)$/);
  if (!matched) return null;
  return {
    sceneNumberWithinEpisode: Number.parseInt(matched[1], 10),
    suffix: matched[2] || "",
  };
};

const buildSceneIdNormalised = (episodeNumber: number | null, sceneWithinEpisode: unknown) => {
  if (!episodeNumber) return null;
  const parsed = normalizeSceneWithinEpisode(sceneWithinEpisode);
  if (!parsed || !Number.isFinite(parsed.sceneNumberWithinEpisode)) return null;
  return `E${String(episodeNumber).padStart(2, "0")}_SC${String(parsed.sceneNumberWithinEpisode).padStart(3, "0")}${parsed.suffix}`;
};

const buildBidSceneIndexes = (scenes: Scene[], sequences: Sequence[]) => {
  const sceneByNormalised = new Map<
    string,
    {
      sceneId: string;
      sequenceId?: string;
      sequenceTitle?: string;
      episodeId?: string;
      heading?: string;
      description?: string;
      locationName?: string;
      intExt?: string;
      characters?: string[];
    }
  >();
  const sequenceByDerivedScene = new Map<string, Sequence>();
  sequences.forEach((sequence) => {
    if (sequence.derivedFromSceneNumber) {
      sequenceByDerivedScene.set(String(sequence.derivedFromSceneNumber).trim().toUpperCase(), sequence);
    }
  });

  scenes.forEach((scene) => {
    const direct = parseSceneNumberRule(scene.sceneNumber);
    const episodeNumber = parseEpisodeNumberFromValue(scene.episodeId);
    const fallbackNormalised = buildSceneIdNormalised(episodeNumber, scene.sceneNumber);
    const sceneIdNormalised = direct.sceneIdNormalised || fallbackNormalised;
    if (sceneIdNormalised) {
      const linkedSequence = scene.sequenceId
        ? sequences.find((sequence) => sequence.id === scene.sequenceId)
        : sequenceByDerivedScene.get(String(scene.sceneNumber).trim().toUpperCase());
      sceneByNormalised.set(sceneIdNormalised, {
        sceneId: scene.id,
        sequenceId: linkedSequence?.id || scene.sequenceId,
        sequenceTitle: linkedSequence?.title || linkedSequence?.code,
        episodeId: scene.episodeId,
        heading: scene.heading,
        description: scene.description,
        locationName: scene.locationName,
        intExt: scene.intExt,
        characters: scene.characters,
      });
    }
  });

  return sceneByNormalised;
};

const parseSceneRowString = (row: any) => {
  const rawText = String(row?.raw_text || row?.rawExtractedText || "").trim();
  const rawSource = rawText || String(row || "").trim();
  if (!rawSource) return null;

  const originalSceneNumber =
    row?.original_scene_number ||
    row?.originalSceneNumber ||
    rawSource.match(/^\s*(\d+\s*\/\s*\d+[A-Z]?)/i)?.[1] ||
    null;
  const intExt = row?.int_ext || row?.intExt || rawSource.match(/\b(INT\/EXT|EXT\/INT|INT|EXT|I\/E)\b/i)?.[1] || null;
  const scriptDayRaw =
    row?.script_day_raw ||
    row?.scriptDayRaw ||
    rawSource.match(/\b((?:D|N|E|VP|SR|DREAM)[A-Z0-9]*)\s*\|\s*([0-9]{1,2}[.:][0-9]{2})/i)?.slice(1).join(" | ") ||
    null;
  const scriptPagesRaw =
    row?.script_pages_raw ||
    row?.scriptPagesRaw ||
    rawSource.match(/\b(\d+(?:\s+\d+\/8|\/8)?)\s*pgs?\b/i)?.[1] ||
    null;
  const castCodesRaw =
    row?.cast_codes_raw ||
    row?.castCodesRaw ||
    rawSource.match(/\b(?:\d+(?:\s+\d+\/8|\/8)?)\s*pgs?\b\s+([A-Z0-9,\s]+?)(?=\s+BACKGROUND ARTISTS?:|\s+FEATURED ARTISTS?:|\s+ACTION VEHICLES?:|$)/i)?.[1]?.trim() ||
    null;
  const locationRaw = row?.location_raw || row?.locationRaw || null;
  const backgroundArtistsCount =
    row?.background_artists_count ??
    parseInteger(rawSource.match(/BACKGROUND ARTISTS?:\s*(\d+)/i)?.[1]);
  const actionVehiclesCount =
    row?.action_vehicles_count ??
    parseInteger(rawSource.match(/ACTION VEHICLES?:\s*(\d+)/i)?.[1]);
  const featuredArtistsCount =
    row?.featured_artists_count ??
    parseInteger(rawSource.match(/FEATURED ARTISTS?:\s*(\d+)/i)?.[1]);

  let setName = row?.set_name || row?.setName || null;
  let description = row?.description || null;
  if (!setName && rawSource) {
    const intMatch = rawSource.match(/\b(?:INT\/EXT|EXT\/INT|INT|EXT|I\/E)\b\s+(.+?)(?=\s+(?:D\d+|N\d+|E\d+|VP|SR|DREAM)\s*\||\s+\d+(?:\s+\d+\/8|\/8)?\s*pgs?\b|$)/i);
    if (intMatch) {
      const segment = intMatch[1].trim();
      const descMatch = segment.match(/^([A-Z0-9,'’&()\- ]{4,}?)(?:\s{2,}|(?<=[A-Z])\s(?=[A-Z][a-z]))(.*)$/);
      if (descMatch) {
        setName = descMatch[1]?.trim() || setName;
        description = description || descMatch[2]?.trim() || null;
      } else {
        setName = segment;
      }
    }
  }

  return {
    original_scene_number: originalSceneNumber,
    int_ext: intExt,
    set_name: setName,
    description,
    script_day_raw: scriptDayRaw,
    script_pages_raw: scriptPagesRaw,
    cast_codes_raw: castCodesRaw,
    location_raw: locationRaw,
    background_artists_count: backgroundArtistsCount,
    action_vehicles_count: actionVehiclesCount,
    featured_artists_count: featuredArtistsCount,
    item_notes: cleanArray(row?.item_notes),
    confidence_score: typeof row?.confidence_score === "number" ? row.confidence_score : null,
    raw_extracted_text: rawSource,
  };
};

const buildIssue = (
  projectId: string,
  scheduleId: string,
  issue: Omit<ScheduleImportIssue, "id" | "projectId" | "scheduleId" | "createdAt" | "updatedAt">,
): ScheduleImportIssue => {
  const now = new Date().toISOString();
  return {
    id: uuidv4(),
    projectId,
    scheduleId,
    createdAt: now,
    updatedAt: now,
    ...issue,
  };
};

export const buildShootingScheduleImportDraft = ({
  projectId,
  documentId,
  fileName,
  fileUrl,
  importedByUserId,
  analysisResult,
  scenes = [],
  sequences = [],
}: {
  projectId: string;
  documentId?: string;
  fileName: string;
  fileUrl?: string | null;
  importedByUserId?: string | null;
  analysisResult: any;
  scenes?: Scene[];
  sequences?: Sequence[];
}): ShootingSchedule => {
  const source = analysisResult?.shooting_schedule_data || {};
  const now = new Date().toISOString();
  const scheduleId = uuidv4();
  const title =
    String(source.schedule_title || source.title || analysisResult?.global_fields?.project_title || fileName).trim() ||
    fileName;
  const bidSceneIndex = buildBidSceneIndexes(scenes, sequences);

  const castMembers: CastMember[] = (Array.isArray(source.cast_members) ? source.cast_members : []).map((entry: any) => ({
    id: uuidv4(),
    projectId,
    scheduleId,
    castCode: String(entry?.cast_code || entry?.code || "").trim(),
    characterName: String(entry?.character_name || entry?.name || entry?.raw_label || "").trim(),
    rawLabel: entry?.raw_label ? String(entry.raw_label) : null,
    type: inferCastMemberType(String(entry?.cast_code || entry?.code || ""), entry?.raw_label || entry?.character_name || entry?.name) || "unknown",
    createdAt: now,
    updatedAt: now,
  })).filter((member) => member.castCode || member.characterName);

  const shootWeeks: ShootWeek[] = [];
  const shootDays: ShootDay[] = [];
  const scheduledScenes: ScheduledScene[] = [];
  const issues: ScheduleImportIssue[] = [];

  const dayEntries = Array.isArray(source.shoot_days)
    ? source.shoot_days
    : (Array.isArray(source.shoot_weeks)
        ? source.shoot_weeks.flatMap((week: any) =>
            Array.isArray(week?.shoot_days)
              ? week.shoot_days.map((day: any) => ({ ...day, __week_number: week?.week_number, __week_start: week?.start_date, __week_end: week?.end_date }))
              : [],
          )
        : []);

  const weekMap = new Map<number, ShootWeek>();

  dayEntries.forEach((entry: any, dayIndex: number) => {
    const weekNumber = parseInteger(entry?.__week_number ?? entry?.week_number) ?? Math.floor(dayIndex / 7) + 1;
    let shootWeekId = weekMap.get(weekNumber)?.id;
    if (!shootWeekId) {
      const weekId = uuidv4();
      weekMap.set(weekNumber, {
        id: weekId,
        projectId,
        scheduleId,
        weekNumber,
        startDate: String(entry?.__week_start || entry?.week_start || entry?.shoot_date || "").trim(),
        endDate: String(entry?.__week_end || entry?.week_end || entry?.shoot_date || "").trim(),
        createdAt: now,
        updatedAt: now,
      });
      shootWeekId = weekId;
    }

    const shootDayId = uuidv4();
    const shootDay: ShootDay = {
      id: shootDayId,
      projectId,
      scheduleId,
      shootWeekId,
      shootDayNumber: parseInteger(entry?.shoot_day_number),
      shootDate: String(entry?.shoot_date || "").trim(),
      weekday: entry?.weekday ? String(entry.weekday).trim() : null,
      callTimeStart: entry?.call_time_start ? String(entry.call_time_start).trim() : null,
      callTimeEnd: entry?.call_time_end ? String(entry.call_time_end).trim() : null,
      dayType: normalizeDayType(entry?.day_type),
      dawnTime: entry?.dawn_time ? String(entry.dawn_time).trim() : null,
      sunriseTime: entry?.sunrise_time ? String(entry.sunrise_time).trim() : null,
      sunsetTime: entry?.sunset_time ? String(entry.sunset_time).trim() : null,
      duskTime: entry?.dusk_time ? String(entry.dusk_time).trim() : null,
      dayNotes: cleanArray(entry?.day_notes),
      unavailableCastNotes: cleanArray(entry?.unavailable_cast_notes),
      totalScriptPagesRaw: entry?.total_script_pages_raw ? String(entry.total_script_pages_raw).trim() : null,
      totalScriptPagesEighths: parseScriptPageEighths(entry?.total_script_pages_raw),
      confidenceScore: typeof entry?.confidence_score === "number" ? entry.confidence_score : null,
      createdAt: now,
      updatedAt: now,
    };
    shootDays.push(shootDay);

    if (!shootDay.shootDate) {
      issues.push(
        buildIssue(projectId, scheduleId, {
          severity: "error",
          entityType: "shoot_day",
          entityId: shootDayId,
          message: "Shoot day is missing a date.",
          rawText: entry?.raw_text ? String(entry.raw_text) : null,
          sourcePageNumber: parseInteger(entry?.source_page_number),
          resolved: false,
        }),
      );
    }

    const rowScenes = Array.isArray(entry?.scenes)
      ? entry.scenes
      : Array.isArray(entry?.scene_rows)
        ? entry.scene_rows
            .map((row: any) => parseSceneRowString(row))
            .filter(Boolean)
        : [];
    rowScenes.forEach((sceneEntry: any, sceneIndex: number) => {
      const parsedSceneNumber = parseSceneNumberRule(sceneEntry?.original_scene_number || sceneEntry?.scene_number);
      const linkedBidScene = parsedSceneNumber.sceneIdNormalised
        ? bidSceneIndex.get(parsedSceneNumber.sceneIdNormalised)
        : undefined;
      const sceneId = uuidv4();
      const castCodesRaw = String(sceneEntry?.cast_codes_raw || sceneEntry?.cast_codes || "").trim();
      const sceneRecord: ScheduledScene = {
        id: sceneId,
        projectId,
        scheduleId,
        shootDayId,
        shootWeekId,
        ...parsedSceneNumber,
        intExt: sceneEntry?.int_ext ? String(sceneEntry.int_ext).trim() : linkedBidScene?.intExt || null,
        setName: sceneEntry?.set_name ? String(sceneEntry.set_name).trim() : linkedBidScene?.locationName || null,
        sceneHeadingRaw: sceneEntry?.scene_heading_raw ? String(sceneEntry.scene_heading_raw).trim() : linkedBidScene?.heading || null,
        description: sceneEntry?.description ? String(sceneEntry.description).trim() : linkedBidScene?.description || null,
        scriptPagesRaw: sceneEntry?.script_pages_raw ? String(sceneEntry.script_pages_raw).trim() : null,
        scriptPagesEighths: parseScriptPageEighths(sceneEntry?.script_pages_raw),
        castCodesRaw: castCodesRaw || null,
        castCodes: normalizeCastCodes(castCodesRaw),
        specialRequirements: cleanArray(sceneEntry?.special_requirements),
        scriptDayRaw: sceneEntry?.script_day_raw ? String(sceneEntry.script_day_raw).trim() : null,
        scriptDayType: normalizeScriptDayType(sceneEntry?.script_day_type),
        scriptDayNumber: parseInteger(sceneEntry?.script_day_number),
        scriptTime: sceneEntry?.script_time ? String(sceneEntry.script_time).trim() : null,
        locationRaw: sceneEntry?.location_raw ? String(sceneEntry.location_raw).trim() : null,
        locationName: sceneEntry?.location_name ? String(sceneEntry.location_name).trim() : linkedBidScene?.locationName || null,
        locationAddress: sceneEntry?.location_address ? String(sceneEntry.location_address).trim() : null,
        locationStatus: normalizeLocationStatus(sceneEntry?.location_status || sceneEntry?.location_raw || sceneEntry?.location_name),
        backgroundArtistsCount: parseInteger(sceneEntry?.background_artists_count),
        actionVehiclesCount: parseInteger(sceneEntry?.action_vehicles_count),
        featuredArtistsCount: parseInteger(sceneEntry?.featured_artists_count),
        itemNotes: cleanArray(sceneEntry?.item_notes),
        partLabel: sceneEntry?.part_label ? String(sceneEntry.part_label).trim() : null,
        isOmitted: Boolean(sceneEntry?.is_omitted),
        isUnscheduled: Boolean(sceneEntry?.is_unscheduled),
        isRepeated: Boolean(sceneEntry?.is_repeated),
        isIfTimeAllows: Boolean(sceneEntry?.is_if_time_allows),
        sortOrder: dayIndex * 100 + sceneIndex,
        sourcePageNumber: parseInteger(sceneEntry?.source_page_number),
        rawExtractedText: sceneEntry?.raw_extracted_text ? String(sceneEntry.raw_extracted_text) : null,
        confidenceScore: typeof sceneEntry?.confidence_score === "number" ? sceneEntry.confidence_score : null,
        linkedSceneId: linkedBidScene?.sceneId || null,
        linkedSequenceId: linkedBidScene?.sequenceId || null,
        linkedSequenceTitle: linkedBidScene?.sequenceTitle || null,
        linkedEpisodeId: linkedBidScene?.episodeId || null,
        createdAt: now,
        updatedAt: now,
      };
      scheduledScenes.push(sceneRecord);

      if (!sceneRecord.originalSceneNumber) {
        issues.push(
          buildIssue(projectId, scheduleId, {
            severity: "warning",
            entityType: "scheduled_scene",
            entityId: sceneId,
            message: "Scheduled scene is missing an original scene number.",
            rawText: sceneRecord.rawExtractedText,
            sourcePageNumber: sceneRecord.sourcePageNumber,
            resolved: false,
          }),
        );
      }

      if (sceneRecord.originalSceneNumber && !sceneRecord.linkedSceneId) {
        issues.push(
          buildIssue(projectId, scheduleId, {
            severity: "info",
            entityType: "scheduled_scene",
            entityId: sceneId,
            message: `No bid scene match found for ${sceneRecord.originalSceneNumber}.`,
            rawText: sceneRecord.rawExtractedText,
            sourcePageNumber: sceneRecord.sourcePageNumber,
            resolved: false,
          }),
        );
      }
    });
  });

  const extraSceneBuckets = [
    ...(Array.isArray(source?.omitted_scenes) ? source.omitted_scenes.map((scene: any) => ({ ...scene, is_omitted: true })) : []),
    ...(Array.isArray(source?.unscheduled_scenes) ? source.unscheduled_scenes.map((scene: any) => ({ ...scene, is_unscheduled: true })) : []),
  ];
  extraSceneBuckets.forEach((sceneEntry: any, index: number) => {
    const parsedSceneNumber = parseSceneNumberRule(sceneEntry?.original_scene_number || sceneEntry?.scene_number);
    const linkedBidScene = parsedSceneNumber.sceneIdNormalised
      ? bidSceneIndex.get(parsedSceneNumber.sceneIdNormalised)
      : undefined;
    scheduledScenes.push({
      id: uuidv4(),
      projectId,
      scheduleId,
      ...parsedSceneNumber,
      intExt: sceneEntry?.int_ext ? String(sceneEntry.int_ext).trim() : null,
      setName: sceneEntry?.set_name ? String(sceneEntry.set_name).trim() : null,
      sceneHeadingRaw: sceneEntry?.scene_heading_raw ? String(sceneEntry.scene_heading_raw).trim() : null,
      description: sceneEntry?.description ? String(sceneEntry.description).trim() : null,
      scriptPagesRaw: sceneEntry?.script_pages_raw ? String(sceneEntry.script_pages_raw).trim() : null,
      scriptPagesEighths: parseScriptPageEighths(sceneEntry?.script_pages_raw),
      castCodesRaw: sceneEntry?.cast_codes_raw ? String(sceneEntry.cast_codes_raw).trim() : null,
      castCodes: normalizeCastCodes(sceneEntry?.cast_codes_raw),
      specialRequirements: cleanArray(sceneEntry?.special_requirements),
      scriptDayRaw: sceneEntry?.script_day_raw ? String(sceneEntry.script_day_raw).trim() : null,
      scriptDayType: normalizeScriptDayType(sceneEntry?.script_day_type),
      scriptDayNumber: parseInteger(sceneEntry?.script_day_number),
      scriptTime: sceneEntry?.script_time ? String(sceneEntry.script_time).trim() : null,
      locationRaw: sceneEntry?.location_raw ? String(sceneEntry.location_raw).trim() : null,
      locationName: sceneEntry?.location_name ? String(sceneEntry.location_name).trim() : null,
      locationAddress: sceneEntry?.location_address ? String(sceneEntry.location_address).trim() : null,
      locationStatus: normalizeLocationStatus(sceneEntry?.location_status || sceneEntry?.location_raw || sceneEntry?.location_name),
      backgroundArtistsCount: parseInteger(sceneEntry?.background_artists_count),
      actionVehiclesCount: parseInteger(sceneEntry?.action_vehicles_count),
      featuredArtistsCount: parseInteger(sceneEntry?.featured_artists_count),
      itemNotes: cleanArray(sceneEntry?.item_notes),
      partLabel: sceneEntry?.part_label ? String(sceneEntry.part_label).trim() : null,
      isOmitted: Boolean(sceneEntry?.is_omitted),
      isUnscheduled: Boolean(sceneEntry?.is_unscheduled),
      isRepeated: Boolean(sceneEntry?.is_repeated),
      isIfTimeAllows: Boolean(sceneEntry?.is_if_time_allows),
      sortOrder: 10_000 + index,
      sourcePageNumber: parseInteger(sceneEntry?.source_page_number),
      rawExtractedText: sceneEntry?.raw_extracted_text ? String(sceneEntry.raw_extracted_text) : null,
      confidenceScore: typeof sceneEntry?.confidence_score === "number" ? sceneEntry.confidence_score : null,
      linkedSceneId: linkedBidScene?.sceneId || null,
      linkedSequenceId: linkedBidScene?.sequenceId || null,
      linkedSequenceTitle: linkedBidScene?.sequenceTitle || null,
      linkedEpisodeId: linkedBidScene?.episodeId || null,
      createdAt: now,
      updatedAt: now,
    });
  });

  const weeks = Array.from(weekMap.values()).map((week) => {
    const daysForWeek = shootDays
      .filter((day) => day.shootWeekId === week.id)
      .sort((left, right) => left.shootDate.localeCompare(right.shootDate));
    return {
      ...week,
      startDate: week.startDate || daysForWeek[0]?.shootDate || "",
      endDate: week.endDate || daysForWeek[daysForWeek.length - 1]?.shootDate || "",
    };
  });
  shootWeeks.push(...weeks);

  cleanArray(source?.warnings).forEach((warning) => {
    issues.push(
      buildIssue(projectId, scheduleId, {
        severity: "warning",
        entityType: "schedule",
        entityId: scheduleId,
        message: warning,
        rawText: null,
        sourcePageNumber: null,
        resolved: false,
      }),
    );
  });

  return {
    id: scheduleId,
    projectId,
    title,
    slug: toSlug(title),
    parentId: null,
    order: 0,
    content: null,
    contentFormat: "structured",
    tags: ["shooting-schedule"],
    category: "production",
    visibility: "all-staff",
    allowedRoles: [],
    allowedDepartments: [],
    sourceDocumentId: documentId || null,
    sourceFileName: fileName,
    sourceFileUrl: fileUrl || null,
    sourceFileStoragePath: null,
    scheduleDate: String(source.schedule_date || "").trim() || null,
    importedAt: now,
    importedByUserId: importedByUserId || null,
    createdBy: importedByUserId || null,
    updatedBy: importedByUserId || null,
    status: "draft",
    extractionVersion: EXTRACTION_VERSION,
    parserVersion: PARSER_VERSION,
    sourcePageCount: parseInteger(source.source_page_count),
    productionTitle: String(source.production_title || analysisResult?.global_fields?.project_title || "").trim() || null,
    rawScheduleNotes: cleanArray(source.raw_schedule_notes),
    confidenceScore: typeof source.confidence_score === "number" ? source.confidence_score : null,
    warnings: cleanArray(source.warnings),
    castMembers,
    shootWeeks,
    shootDays,
    scheduledScenes,
    issues,
    createdAt: now,
    updatedAt: now,
  };
};
